import { ReactNode } from 'react'
import { ApiException } from '../hooks/useApi'

interface ErrorDisplayProps {
  error: Error | ApiException | string | null
  title?: string
  showRetry?: boolean
  onRetry?: () => void
  className?: string
  children?: ReactNode
}

export default function ErrorDisplay({
  error,
  title = 'Error',
  showRetry = false,
  onRetry,
  className = '',
  children
}: ErrorDisplayProps) {
  if (!error) return null

  const getErrorMessage = (err: Error | ApiException | string): string => {
    if (typeof err === 'string') return err
    if (err instanceof ApiException) return err.message
    return err.message || 'An unexpected error occurred'
  }

  const getErrorDetails = (err: Error | ApiException | string): Record<string, string[]> | undefined => {
    if (err instanceof ApiException) return err.details
    return undefined
  }

  const errorMessage = getErrorMessage(error)
  const errorDetails = getErrorDetails(error)

  return (
    <div className={`error-display ${className}`} style={{
      backgroundColor: 'rgba(255, 82, 82, 0.1)',
      border: '1px solid var(--accent-red)',
      borderRadius: '8px',
      padding: '1rem',
      margin: '1rem 0'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '0.75rem'
      }}>
        <div style={{
          color: 'var(--accent-red)',
          fontSize: '1.25rem',
          lineHeight: 1,
          marginTop: '0.125rem'
        }}>
          ⚠️
        </div>
        
        <div style={{ flex: 1 }}>
          <h4 style={{
            color: 'var(--accent-red)',
            margin: '0 0 0.5rem 0',
            fontSize: '1rem',
            fontWeight: '600'
          }}>
            {title}
          </h4>
          
          <p style={{
            color: 'var(--text-light)',
            margin: '0 0 0.75rem 0',
            lineHeight: 1.4
          }}>
            {errorMessage}
          </p>

          {errorDetails && Object.keys(errorDetails).length > 0 && (
            <div style={{ marginBottom: '0.75rem' }}>
              <h5 style={{
                color: 'var(--text-muted)',
                fontSize: '0.875rem',
                margin: '0 0 0.5rem 0',
                fontWeight: '500'
              }}>
                Details:
              </h5>
              <ul style={{
                margin: 0,
                paddingLeft: '1.25rem',
                color: 'var(--text-light)',
                fontSize: '0.875rem'
              }}>
                {Object.entries(errorDetails).map(([field, messages]) => (
                  <li key={field} style={{ marginBottom: '0.25rem' }}>
                    <strong>{field}:</strong> {messages.join(', ')}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {children && (
            <div style={{ marginBottom: '0.75rem' }}>
              {children}
            </div>
          )}

          {showRetry && onRetry && (
            <button
              onClick={onRetry}
              style={{
                backgroundColor: 'var(--accent-red)',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: '0.5rem 1rem',
                fontSize: '0.875rem',
                cursor: 'pointer',
                fontWeight: '500'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#e53e3e'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--accent-red)'
              }}
            >
              Try Again
            </button>
          )}
        </div>
      </div>
    </div>
  )
}